import { toast } from 'react-toastify';
import { clientFetch } from '~/lib/.client/fetch';
import { chatStore } from '~/lib/stores/chat';
import type { Message } from '~/types/chat';
import type { OrganizationData } from '~/types/organization';

interface APIChat {
  projectId: string;
  description: string;
  createdAt: number;
  updatedAt: number;
}

export interface ChatHistoryItem {
  projectId: string;
  description?: string;
  messages: Message[];
  timestamp: number;
}

export async function getAllChats(activeOrganization: OrganizationData | null): Promise<ChatHistoryItem[]> {
  const url = new URL('/api/chats', window.origin);

  if (activeOrganization) {
    url.searchParams.set('organization', String(activeOrganization.id));
  }

  const response = await clientFetch(url);

  if (!response.ok) {
    // todo maybe do something else, like show an error?
    toast.error('Failed to load chats.');

    return [];
  }

  const data = await response.json<{ chats: APIChat[] }>();

  return data.chats.map<ChatHistoryItem>((item) => {
    return {
      ...item,
      timestamp: item.updatedAt,
      messages: [],
    };
  });
}

export type ChatHistory =
  | undefined
  | {
      editedAt?: Date;
      messages: Message[];
      hasMore: boolean;
    };

export async function createChat({
  projectId,
  description,
  activeOrganization,
}: {
  projectId: string;
  description: string | undefined;
  activeOrganization: OrganizationData | null;
}) {
  const url = new URL('/api/chats', window.origin);

  if (activeOrganization) {
    url.searchParams.set('organization', String(activeOrganization.id));
  }

  const response = await clientFetch(url, {
    method: 'POST',
    body: JSON.stringify({ projectId, description }),
  });

  if (!response.ok) {
    throw new Error('Failed to persist the chat.');
  }

  try {
    await chatStore.loadChatList();
  } catch {
    // do nothing if reloading the chat list fails
  }
}

export async function updateChatHistory(projectId: string, messages: Message[]) {
  const response = await clientFetch(`/api/chats/${projectId}`, {
    method: 'POST',
    body: JSON.stringify({
      messages,
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to persist the chat messages.');
  }

  const chatList = chatStore.chatList.get();

  // we will only update the chat list if the updated chat is not the first one
  if (chatList[0]?.projectId !== projectId) {
    const updatedChatList = chatList.reduce((list, chat) => {
      if (chat.projectId === projectId) {
        // we sort the list by timestamp so we need to update it
        list.unshift({ ...chat, timestamp: Date.now() });
      } else {
        list.push(chat);
      }

      return list;
    }, [] as ChatHistoryItem[]);

    chatStore.chatList.set(updatedChatList);
  }
}

export async function getChatHistory(
  projectId: string,
  activeOrganization: OrganizationData | null,
  lastMessageOrder?: number,
): Promise<ChatHistory | undefined> {
  const url = new URL(`/api/chats/${projectId}`, window.origin);

  if (activeOrganization) {
    url.searchParams.set('organization', String(activeOrganization.id));
  }

  if (typeof lastMessageOrder === 'number') {
    url.searchParams.set('lastMessageOrder', String(lastMessageOrder));
  }

  const response = await clientFetch(url);

  if (!response.ok) {
    // todo maybe do something else, like show an error?

    return undefined;
  }

  const data = await response.json<{
    editedAt?: number;
    messages: Message[];
    hasMore: boolean;
  }>();

  const skipAllActions = data.messages.length > 1;

  return {
    editedAt: data.editedAt ? new Date(data.editedAt) : undefined,
    messages: data.messages.map((message) => {
      return {
        ...message,
        status: 'complete' as const,
        skipAction: skipAllActions,
      };
    }),
    hasMore: data.hasMore,
  };
}

export async function removeChat(projectId: string) {
  const response = await clientFetch(`/api/chats/${projectId}`, {
    method: 'DELETE',
  });

  if (!response.ok) {
    toast.error('Failed to remove the chat.');
  }
}

export async function removeChats(activeOrganization: OrganizationData | null) {
  const url = new URL(`/api/chats`, window.origin);

  if (activeOrganization) {
    url.searchParams.set('organization', String(activeOrganization.id));
  }

  const response = await clientFetch(url, {
    method: 'DELETE',
  });

  if (!response.ok) {
    toast.error('Failed to remove the chats.');
  }
}

export interface UpdateChatData {
  editedAt?: string;
  description?: string;
}

let nextUpdate: UpdateChatData | undefined;

let updatePromise = Promise.resolve();

export async function updateChat(projectId: string, data: UpdateChatData) {
  const body: Record<string, string | number> = {};

  if (data.editedAt) {
    body.editedAt = new Date(data.editedAt).getTime();
  }

  if (data.description) {
    body.description = data.description;
  }

  if (Object.keys(body).length === 0) {
    return updatePromise;
  }

  if (nextUpdate) {
    // if we already have a next update scheduled, we overwrite that data with the latest and early exit
    nextUpdate = body;

    return updatePromise;
  }

  // if no next update is scheduled, we schedule the update
  nextUpdate = body;

  updatePromise = updatePromise.then(async () => {
    if (!nextUpdate) {
      // this should never happen but you never know
      return;
    }

    const body = JSON.stringify(nextUpdate);

    // we reset the next update so we can schedule a new one
    nextUpdate = undefined;

    await clientFetch(`/api/chats/${projectId}`, {
      method: 'PUT',
      body,
    });
  });

  return updatePromise;
}
